// What the admin page works out from LINQ's usage records: what changed on a day, and how an organisation uses LINQ,
// with points to raise when talking to the customer. Pure, so it can be tested without the page.

import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { isSameDay } from 'date-fns/isSameDay'
import { MODEL_CHANGE, ModelAnalytics, ORGANISATION_STATE } from '../../common/organisation'
import { USER_ROLE } from '../../common/user'

// what the server returns for an organisation (see AdminServices.getOrganisationUsage)
export interface OrganisationUsage {
    days: number
    organisation: { _key: string, name: string, createdDate: Date, lastAccess: Date, endDate?: Date, state: ORGANISATION_STATE, isPartner: boolean, isExemptFromPayment: boolean, notCustomer: boolean }
    // lastActive: the latest of opening the organisation, signing in, saving a change and recent requests
    users: { _key: string, name: string, email: string, access?: USER_ROLE, createdDate: Date, signInDate?: Date, lastActive?: Date, activated: boolean }[]
    models: { _key: string, name: string, function: string, inProject: boolean, createdDate: Date, lastChanged?: Date, lastChangedBy?: string }[]
    projects: { _key: string, name: string, startDate: Date, sandbox: boolean }[]
    analytics: ModelAnalytics[]
    recentActivity: { email: string, name: string, organisation: string, action: string, timestamp: Date, byAdmin?: boolean }[]
    totals: { actionNodes: number, hours: number, cost: number }
}

const CHANGE_NAMES: { [type: number]: string } = {
    [MODEL_CHANGE.OTHER]: 'Other change',
    [MODEL_CHANGE.UPDATE_MODEL]: 'Changed a model',
    [MODEL_CHANGE.UPLOAD_GRAPH]: 'Uploaded a model',
    [MODEL_CHANGE.REPLACE_CURRENT_STATE]: 'Replaced current state',
    [MODEL_CHANGE.DELETE_MODEL]: 'Deleted a model',
    [MODEL_CHANGE.CREATE_PROJECT]: 'Created a project',
    [MODEL_CHANGE.DELETE_PROJECT]: 'Deleted a project',
    [MODEL_CHANGE.BACKUP_MODEL]: 'Backed up a model',
    [MODEL_CHANGE.RESTORE_MODEL]: 'Restored a model',
    [MODEL_CHANGE.DELETE_BACKUP]: 'Deleted a backup',
    [MODEL_CHANGE.CLOSE_ORGANISATION]: 'Closed the organisation',
    [MODEL_CHANGE.REMOVE_USER]: 'Removed a user',
    [MODEL_CHANGE.ADD_USER]: 'Added a user',
    [MODEL_CHANGE.BACKUP_ORG]: 'Backed up the organisation',
    [MODEL_CHANGE.RESTORE_ORG_BACKUP]: 'Restored an organisation backup',
    [MODEL_CHANGE.DELETE_ORG_BACKUP]: 'Deleted an organisation backup',
    [MODEL_CHANGE.COPY_MODEL]: 'Copied a model',
}

export const changeName = (type: MODEL_CHANGE) => CHANGE_NAMES[type] ?? 'Other change'

// a change as a row: who did what to which model, and how much
export interface ChangeRow {
    _key: string
    time: Date
    organisationKey: string
    organisation: string
    person: string
    change: string
    model: string
    added: number
    changed: number
    deleted: number
}

export const changeRow = (a: ModelAnalytics, organisationName = ''): ChangeRow => ({
    _key: a._key,
    time: new Date(a.createdDate),
    organisationKey: a.organisationKey,
    organisation: organisationName,
    person: a.userName || '',
    change: changeName(a.type),
    model: a.modelName || '',
    added: (a.newNodes || 0) + (a.newEdges || 0),
    changed: (a.nodeChanges || 0) + (a.edgeChanges || 0),
    deleted: (a.deletedNodes || 0) + (a.deletedEdges || 0),
})

const newestFirst = (a: { time: Date }, b: { time: Date }) => b.time.getTime() - a.time.getTime()

// the changes made on a day, newest first, with each organisation's name
export const changesOnDay = (analytics: ModelAnalytics[], day: Date, organisationNames: { [key: string]: string }): ChangeRow[] =>
    (analytics || []).filter(a => isSameDay(new Date(a.createdDate), day))
        .map(a => changeRow(a, organisationNames[a.organisationKey] || 'Unknown organisation'))
        .sort(newestFirst)

// the organisations busiest on a day, most changes first
export const organisationsOnDay = (rows: ChangeRow[]): { organisationKey: string, organisation: string, changes: number, people: string[] }[] => {
    const byOrganisation = new Map<string, { organisationKey: string, organisation: string, changes: number, people: string[] }>()
    rows.forEach(r => {
        const o = byOrganisation.get(r.organisationKey) || { organisationKey: r.organisationKey, organisation: r.organisation, changes: 0, people: [] }
        o.changes++
        if (r.person && !o.people.includes(r.person)) o.people.push(r.person)
        byOrganisation.set(r.organisationKey, o)
    })
    return [...byOrganisation.values()].sort((a, b) => b.changes - a.changes)
}

// ---------- an organisation's usage ----------

export interface UsageSummary {
    users: number
    activeUsers30: number           // active during the last 30 days
    inactiveUsers: string[]          // active before, but not for 30 days
    neverSignedIn: string[]          // invited or registered but not activated, or never active
    changes7: number
    changes30: number
    lastChange?: Date
    daysSinceChange?: number
    mostActive?: { name: string, changes: number }      // in the last 30 days
    models: number
    modelsChanged30: number
    staleModels: string[]            // not changed for 60 days
    projects: number
    actionNodes: number
}

export interface TalkingPoint {
    tone: 'good' | 'warn' | 'idea'
    text: string
}

const within = (date: Date | undefined, days: number, now: Date) => !!date && differenceInCalendarDays(now, new Date(date)) < days
const names = (list: string[]) => list.length <= 2 ? list.join(' and ') : `${list.slice(0, 2).join(', ')} and ${list.length - 2} more`

// when someone was last active, from the server's reckoning or, failing that, their sign in
const lastActive = (u: OrganisationUsage['users'][number]) => u.lastActive ?? u.signInDate

export const summariseUsage = (usage: OrganisationUsage, now: Date = new Date()): UsageSummary => {
    const analytics = (usage.analytics || []).filter(a => a.type !== MODEL_CHANGE.OTHER || a.nodeChanges || a.newNodes)
    const recent = analytics.filter(a => within(a.createdDate, 30, now))
    const byPerson = new Map<string, number>()
    recent.forEach(a => a.userName && byPerson.set(a.userName, (byPerson.get(a.userName) || 0) + 1))
    const busiest = [...byPerson.entries()].sort((a, b) => b[1] - a[1])[0]
    const lastChange = analytics.map(a => new Date(a.createdDate)).sort((a, b) => b.getTime() - a.getTime())[0]
        ?? usage.models.map(m => m.lastChanged && new Date(m.lastChanged)).filter((d): d is Date => !!d).sort((a, b) => b.getTime() - a.getTime())[0]

    return {
        users: usage.users.length,
        activeUsers30: usage.users.filter(u => within(lastActive(u), 30, now)).length,
        inactiveUsers: usage.users.filter(u => u.activated && lastActive(u) && !within(lastActive(u), 30, now)).map(u => u.name),
        neverSignedIn: usage.users.filter(u => !u.activated || !lastActive(u)).map(u => u.name),
        changes7: analytics.filter(a => within(a.createdDate, 7, now)).length,
        changes30: recent.length,
        lastChange,
        daysSinceChange: lastChange ? differenceInCalendarDays(now, lastChange) : undefined,
        mostActive: busiest ? { name: busiest[0], changes: busiest[1] } : undefined,
        models: usage.models.length,
        modelsChanged30: usage.models.filter(m => within(m.lastChanged, 30, now)).length,
        staleModels: usage.models.filter(m => !within(m.lastChanged || m.createdDate, 60, now)).map(m => m.name),
        projects: usage.projects.length,
        actionNodes: usage.totals?.actionNodes || 0,
    }
}

// what to raise with the customer, most pressing first: things going well to build on, risks to their use of LINQ,
// and features they haven't tried
export const talkingPoints = (usage: OrganisationUsage, summary: UsageSummary, now: Date = new Date()): TalkingPoint[] => {
    const points: TalkingPoint[] = []
    const o = usage.organisation

    if (o.state === ORGANISATION_STATE.EXPIRED) points.push({ tone: 'warn', text: 'Their account has expired, so they can no longer use LINQ. Talk to them about renewing.' })
    else if (o.state === ORGANISATION_STATE.NEAR_EXPIRY && o.endDate) points.push({ tone: 'warn', text: `Their account ends in ${differenceInCalendarDays(new Date(o.endDate), now)} days. A good time to talk about renewing.` })

    if (summary.daysSinceChange === undefined) points.push({ tone: 'warn', text: 'They haven\'t changed a model yet. Offer help building their first one, perhaps with AI.' })
    else if (summary.daysSinceChange >= 14) points.push({ tone: 'warn', text: `No model changes for ${summary.daysSinceChange} days. Check in on how they're getting on.` })
    else if (summary.changes7 > 0) points.push({ tone: 'good', text: `Active this week: ${summary.changes7} change${summary.changes7 === 1 ? '' : 's'}${summary.mostActive ? `, most by ${summary.mostActive.name}` : ''}. Ask what they're working on and what would help.` })

    if (summary.neverSignedIn.length) points.push({ tone: 'warn', text: `${names(summary.neverSignedIn)} ${summary.neverSignedIn.length === 1 ? 'hasn\'t' : 'haven\'t'} started using LINQ yet. A reminder of their invitation could help.` })
    if (summary.inactiveUsers.length) points.push({ tone: 'warn', text: `${names(summary.inactiveUsers)} ${summary.inactiveUsers.length === 1 ? 'hasn\'t' : 'haven\'t'} used LINQ for over 30 days.` })
    if (summary.users === 1) points.push({ tone: 'idea', text: 'Only one person uses LINQ. Suggest inviting colleagues who know the processes.' })

    if (summary.models > 0 && summary.projects === 0) points.push({ tone: 'idea', text: 'No projects yet. Suggest modelling a future state, such as generating one from the EPM scorecard\'s decisions.' })
    if (summary.staleModels.length && summary.models > 1) points.push({ tone: 'idea', text: `${summary.staleModels.length} of ${summary.models} models haven't changed in 60 days. Check they still reflect how the work is done.` })
    if (summary.models > 0 && summary.actionNodes > 0 && summary.actionNodes < 10) points.push({ tone: 'idea', text: `Their models are small (${summary.actionNodes} actions). Help them add the steps, people and systems that give useful insights.` })
    if (summary.models >= 3 && summary.changes30 > 0) points.push({ tone: 'good', text: `${summary.models} models, ${summary.modelsChanged30} changed in the last 30 days. Show them the Analysis page and EPM scorecard across their models.` })

    return points
}

// people's recent requests, without LINQ administrators' own
export const customerActivity = (usage: OrganisationUsage) => (usage.recentActivity || []).filter(a => !a.byAdmin)
